import { useMemo, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  GlassCard,
  Input,
  StyledSelect,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@jumpstone/ui-react';
import { ScreenHeader } from '../../components/common/screen_header';
import { useSettings } from '../../data/use_settings';
import { format_month_name } from '../../lib/format';
import { navigate } from '../../lib/router';
import type { SettingsTab } from '../../lib/router';
import {
  isCurrencyFieldDisabled,
  resetLockedCurrencyField,
} from './settings_calculations';
import { SettingsPayrollSection } from './settings_payroll_section';
import { LeaveTypesPanel } from './leave_types_panel';
import { SettingsSecuritySection } from './settings_security_section';
import { EmployeeMigrationScreen } from '../employees/import/employee_migration_screen';

/**
 * This screen's own in-page tab strip (mirrors `data_centre_screen.tsx`'s
 * `<Tabs>`) — separate from the App-level top-nav sub-tab strip, which
 * deliberately renders Settings as a plain link with no strip
 * (`nav_model.ts`'s `SUB_TABS` has no `settings` entry; pinned by
 * `nav_model.test.ts`). The strip shows only the tabs with real content
 * (Organisation, Payroll, Leave types, Data import, Security);
 * `'policies'`/`'roles'`/`'operations'` remain reachable only by direct
 * URL/hash and render the Organisation form.
 * `'roles'` has no wiring PLANNED either (not just "not yet built"): its
 * fully-built `RolesMatrix` component was deleted outright rather than left
 * dangling (#22790) -- a real configurable-permission-grid feature would
 * need the entity-membership RBAC model redesigned first, since it's
 * currently a fixed enum-per-user model, not a configurable grid.
 */
const VISIBLE_SETTINGS_TABS: Array<{
  id: 'organisation' | 'payroll' | 'leave_types';
  label_key: string;
}> = [
  { id: 'organisation', label_key: 'settings.tab_organisation' },
  { id: 'payroll', label_key: 'settings.tab_payroll' },
  { id: 'leave_types', label_key: 'settings.tab_leave_types' },
];

/** 1-based calendar months, the shape the fiscal-year setting is stored in. */
const MONTH_NUMBERS = Array.from({ length: 12 }, (_, index) => index + 1);

/**
 * Trigger styling for a `StyledSelect`. The prop replaces the component's
 * default classes rather than merging with them, so the base `nv-input` shell
 * has to be spelled out here — the same way every Ledger call site does it.
 * Passing only a width utility strips the shell and the button falls back to
 * UA styling (centred label, chevron wrapping onto its own line).
 */
const SELECT_TRIGGER_CLASSES =
  'nv-input flex h-9 max-w-[200px] cursor-pointer items-center justify-between gap-2';

/**
 * Organisation settings screen — editable employer details, wired to the
 * real `entities.update` mutation via the real production bearer client
 * (C6, #21937; C11, #22333), with a mock fallback whenever no real
 * controller is wired (e.g. a test harness mounting `WageProStoreProvider`
 * directly). `<StyledSelect>` always speaks
 * UI/start-month units for the fiscal year — the end-month<->start-month
 * conversion happens entirely inside `wagepro_store_settings.ts`'s pure
 * `fyEndMonthToStartMonth`/`fyStartMonthToEndMonth`, never in this
 * component.
 *
 * The `locale` field that used to live on this screen was dropped (see
 * `types.ts`'s `OrganisationSettings` doc comment) — `entities` has no
 * locale column and no consumer exists for one anywhere in the codebase.
 */
export function SettingsScreen({
  tab,
}: {
  tab: SettingsTab;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const {
    organisation,
    update_organisation,
    is_saving_organisation,
    has_posted_pay_run,
  } = useSettings();

  // Month names come from CLDR via `Intl`, so the picker follows the active
  // locale without a translated key per month.
  const month_options = useMemo(
    () =>
      MONTH_NUMBERS.map((month) => ({
        value: String(month),
        label: format_month_name(month, locale),
      })),
    [locale],
  );

  const [draft, set_draft] = useState(organisation);
  const [saved, set_saved] = useState(false);
  const [currency_locked, set_currency_locked] = useState(false);
  const [save_failed, set_save_failed] = useState(false);

  const currency_disabled = isCurrencyFieldDisabled(
    has_posted_pay_run,
    currency_locked,
  );

  function handle_cancel(): void {
    set_draft(organisation);
    set_currency_locked(false);
    set_save_failed(false);
  }

  async function handle_save(): Promise<void> {
    set_save_failed(false);
    const result = await update_organisation(draft);

    if (result.ok) {
      set_currency_locked(false);
      set_saved(true);
      setTimeout(() => {
        set_saved(false);
      }, 2000);
      return;
    }

    if (result.currency_locked) {
      set_currency_locked(true);
      set_draft((d) => resetLockedCurrencyField(d, organisation));
      // The backend rejects the WHOLE save before writing anything (see
      // `entities.update`'s currency-lock probe) -- if the user also edited
      // the legal name or fiscal year in the same submit, that edit was lost
      // too. Surface the general save-error banner alongside the
      // currency-specific explanation so the user knows the entire save
      // failed, not just that the currency field is locked (adversarial
      // review finding, C11 #22333, second round).
      set_save_failed(true);
      return;
    }

    set_save_failed(true);
  }

  // Any other `SettingsTab` member (`'policies'`/`'roles'`/`'operations'`)
  // collapses to the Organisation tab visually -- see this module's
  // docblock: those three have no real content of their own yet and are
  // reachable only by direct URL, so the strip never highlights them.
  const active_strip_tab:
    | 'organisation'
    | 'payroll'
    | 'leave_types'
    | 'import'
    | 'security' =
    tab === 'payroll'
      ? 'payroll'
      : tab === 'leave_types'
        ? 'leave_types'
        : tab === 'import'
          ? 'import'
          : tab === 'security'
            ? 'security'
            : 'organisation';

  return (
    <>
      <ScreenHeader
        title={t('settings.title')}
        description={t('settings.subtitle')}
      />

      <Tabs
        value={active_strip_tab}
        onValueChange={(next) => {
          navigate({ module: 'settings', tab: next as SettingsTab });
        }}
      >
        <TabsList className="wp-tabstrip mb-3.5">
          {VISIBLE_SETTINGS_TABS.map(({ id, label_key }) => (
            <TabsTrigger key={id} value={id}>
              {t(label_key)}
            </TabsTrigger>
          ))}
          {/* Literal t() calls (not label_key entries above) so the i18n
              orphan check can see these keys are used. Data import is the
              single home of the import wizard, which spans employees,
              salary, payroll history and leave -- no one module owns it. */}
          <TabsTrigger value="import">{t('settings.tab_import')}</TabsTrigger>
          <TabsTrigger value="security">
            {t('settings.tab_security')}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'security' ? (
        <SettingsSecuritySection />
      ) : tab === 'import' ? (
        <EmployeeMigrationScreen />
      ) : tab === 'leave_types' ? (
        <LeaveTypesPanel />
      ) : tab === 'payroll' ? (
        <SettingsPayrollSection />
      ) : (
        <>
          <GlassCard className="rounded-2xl p-5">
            <div className="grid grid-cols-1 gap-5">
              {/* Legal entity */}
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-start sm:items-center gap-1.5 sm:gap-4">
                <label className="nv-label">{t('settings.legal_entity')}</label>
                <Input
                  type="text"
                  value={draft.legal_entity}
                  onChange={(e) => {
                    set_draft((d) => ({ ...d, legal_entity: e.target.value }));
                  }}
                  className="max-w-sm"
                />
              </div>

              <div className="border-t border-[color:var(--line)]" />

              {/* Fiscal year start */}
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-start sm:items-center gap-1.5 sm:gap-4">
                <label className="nv-label">{t('settings.fiscal_year')}</label>
                <StyledSelect
                  value={String(draft.fiscal_year_start_month)}
                  onChange={(v) => {
                    set_draft((d) => ({
                      ...d,
                      fiscal_year_start_month: parseInt(v, 10),
                    }));
                  }}
                  options={month_options}
                  ariaLabel={t('settings.fiscal_year')}
                  triggerClassName={SELECT_TRIGGER_CLASSES}
                />
              </div>

              <div className="border-t border-[color:var(--line)]" />

              {/* Base currency */}
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-start sm:items-center gap-1.5 sm:gap-4">
                <label className="nv-label">
                  {t('settings.base_currency')}
                </label>
                <div className="max-w-[200px]">
                  <Input
                    type="text"
                    value={draft.base_currency}
                    disabled={currency_disabled}
                    onChange={(e) => {
                      set_draft((d) => ({
                        ...d,
                        base_currency: e.target.value,
                      }));
                    }}
                    className="max-w-[120px]"
                  />
                  {currency_disabled && (
                    <p className="nv-help">
                      {t('settings.currency_locked_explanation')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </GlassCard>

          <div className="flex items-center justify-end gap-3 mt-4">
            {save_failed && (
              <p className="nv-help is-error mt-0">
                {t('settings.save_error')}
              </p>
            )}
            <Button variant="ghost" onClick={handle_cancel}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => void handle_save()}
              loading={is_saving_organisation}
            >
              {saved ? t('settings.saved') : t('settings.save')}
            </Button>
          </div>
        </>
      )}
    </>
  );
}
